import { useState, useEffect } from 'react';
import { paidLeaveService, PaidLeave } from '@/lib/storage';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  CheckCircle2,
  XCircle,
  Clock,
  Plus,
  CalendarOff,
  Loader2,
  Trash2,
} from 'lucide-react';
import { cn, getLocalDateString } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

const statusConfig: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
  pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: <Clock className="w-3 h-3" /> },
  approved: { label: 'Approved', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: <CheckCircle2 className="w-3 h-3" /> },
  rejected: { label: 'Rejected', className: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400', icon: <XCircle className="w-3 h-3" /> },
};

export default function StaffPaidLeavesPage() {
  const { session } = useAuth();
  const [leaves, setLeaves] = useState<PaidLeave[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    date: getLocalDateString(),
    reason: '',
  });

  useEffect(() => {
    loadData();
    const interval = setInterval(() => loadData(true), 10000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async (silent = false) => {
    if (!session?.userId) return;
    if (!silent) setLoading(true);
    try {
      const data = await paidLeaveService.getAll({ staffId: session.userId });
      setLeaves(data);
    } catch (error) {
      console.error('Error loading paid leaves:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!session?.userId) return;
    if (!formData.date) {
      toast({ title: 'Please select a date', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await paidLeaveService.create({
        staffId: session.userId,
        staffName: session.name,
        date: formData.date,
        reason: formData.reason || undefined,
      });
      toast({ title: 'Paid leave applied successfully' });
      setDialogOpen(false);
      setFormData({ date: getLocalDateString(), reason: '' });
      await loadData();
    } catch (error) {
      toast({ title: 'Failed to apply for paid leave', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to cancel this paid leave request?')) return;
    try {
      await paidLeaveService.delete(id);
      await loadData();
      toast({ title: 'Paid leave request cancelled' });
    } catch (error) {
      toast({ title: 'Failed to cancel', variant: 'destructive' });
    }
  };

  const pending = leaves.filter((l) => l.status === 'pending');
  const approved = leaves.filter((l) => l.status === 'approved');
  const rejected = leaves.filter((l) => l.status === 'rejected');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">Paid Leave</h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5 sm:mt-1">Apply for paid leaves and track status</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white shrink-0">
          <Plus className="w-4 h-4 mr-1" /> Apply Paid Leave
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card className="bg-white/50 dark:bg-slate-900/40 border-amber-200/50 dark:border-amber-800/30">
          <CardContent className="p-2.5 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3 min-w-0">
            <div className="p-1.5 sm:p-2 rounded-xl bg-amber-500/10 shrink-0">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-base sm:text-2xl font-black text-amber-600 dark:text-amber-400 truncate">{pending.length}</p>
              <p className="text-[9px] sm:text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide truncate">Pending</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/50 dark:bg-slate-900/40 border-emerald-200/50 dark:border-emerald-800/30">
          <CardContent className="p-2.5 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3 min-w-0">
            <div className="p-1.5 sm:p-2 rounded-xl bg-emerald-500/10 shrink-0">
              <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-base sm:text-2xl font-black text-emerald-600 dark:text-emerald-400 truncate">{approved.length}</p>
              <p className="text-[9px] sm:text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide truncate">Approved</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/50 dark:bg-slate-900/40 border-rose-200/50 dark:border-rose-800/30">
          <CardContent className="p-2.5 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3 min-w-0">
            <div className="p-1.5 sm:p-2 rounded-xl bg-rose-500/10 shrink-0">
              <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-rose-600 dark:text-rose-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-base sm:text-2xl font-black text-rose-600 dark:text-rose-400 truncate">{rejected.length}</p>
              <p className="text-[9px] sm:text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide truncate">Rejected</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white/50 dark:bg-slate-900/40 overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          {leaves.length === 0 ? (
            <div className="text-center py-12">
              <CalendarOff className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400">No paid leave requests yet</p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Click "Apply Paid Leave" to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Admin Remark</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaves.map((leave) => (
                  <TableRow key={leave.id}>
                    <TableCell className="font-medium">
                      {new Date(leave.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate text-slate-500 dark:text-slate-400">{leave.reason || '-'}</TableCell>
                    <TableCell>
                      <span className={cn('inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide', statusConfig[leave.status]?.className)}>
                        {statusConfig[leave.status]?.icon}
                        {statusConfig[leave.status]?.label}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate text-slate-500 dark:text-slate-400 text-sm">{leave.adminRemark || '-'}</TableCell>
                    <TableCell className="text-right">
                      {leave.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(leave.id)}
                          className="h-7 px-2 text-[10px] font-bold text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                        >
                          <Trash2 className="w-3 h-3 mr-1" /> Cancel
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Apply for Paid Leave</DialogTitle>
            <DialogDescription>Select a date and provide a reason for your paid leave request.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Leave Date</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800"
              />
              <p className="text-[10px] text-slate-400 font-medium">Past dates are allowed</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Reason (optional)</Label>
              <Textarea
                placeholder="Reason for paid leave..."
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                rows={3}
                className="rounded-xl border-slate-200 dark:border-slate-800"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
